<template>
  <div class="video-downloader-page">
    <div class="header">
      <h2><i class="el-icon-video-camera-solid"></i> 视频下载</h2>
      <p>{{ pageIntroText }}</p>
    </div>

    <div
      class="panel video-downloader-panel"
      v-loading="loading"
      :element-loading-text="panelLoadingHint"
    >
      <el-form label-position="top">
        <el-form-item label="视频链接">
          <el-input
            v-model="videoUrl"
            type="textarea"
            :rows="3"
            :disabled="loading"
            placeholder="可粘贴整段分享文案，会自动识别 b23.tv、bilibili、小红书链接"
          />
        </el-form-item>

        <div class="actions">
          <el-button type="primary" icon="el-icon-download" :loading="loading" @click="startDownload">
            {{ loading ? '下载中...' : '开始下载' }}
          </el-button>
          <el-button plain :disabled="loading" @click="videoUrl = ''">清空</el-button>
        </div>
      </el-form>

      <el-alert
        :title="infoAlertTitle"
        type="info"
        :closable="false"
        show-icon
        class="info-alert"
      />
      <div class="support-tags">
        <span class="support-label">当前支持：</span>
        <el-tag size="mini" effect="plain">B站</el-tag>
        <el-tag size="mini" effect="plain">小红书</el-tag>
      </div>

      <div v-if="result.fileUrl" class="result">
        <h3>下载完成</h3>
        <p><b>文件名：</b>{{ result.filename || '-' }}</p>
        <p><b>标题：</b>{{ result.title || '-' }}</p>
        <div class="result-actions">
          <el-button type="text" class="save-btn" icon="el-icon-download" @click="saveBlobToFile">
            保存到本机
          </el-button>
          <el-button type="text" class="save-btn save-btn-pan" icon="el-icon-upload2" @click="saveToBaiduPan">
            百度网盘免费下载（推荐）
          </el-button>
        </div>
        <div v-if="result.panShareText" class="pan-share-block">
          <p class="pan-share-label"><b>分享口令（整段复制）：</b></p>
          <pre class="pan-share-pre">{{ result.panShareText }}</pre>
        </div>
        <p v-if="result.panShareUrl" class="pan-share-line">
          <b>网盘链接：</b>
          <a :href="result.panShareUrl" target="_blank" rel="noopener noreferrer">{{ result.panShareUrl }}</a>
          <span v-if="result.panSharePwd">（提取码：{{ result.panSharePwd }}）</span>
        </p>
      </div>
    </div>

    <el-dialog title="提示" :visible.sync="showLoginPrompt" width="400px" center append-to-body>
      <p class="login-prompt-text">{{ loginPromptText }}</p>
      <span slot="footer">
        <el-button type="primary" @click="goLogin">去登录</el-button>
        <el-button @click="goRegister">去注册</el-button>
        <el-button @click="showLoginPrompt = false">取 消</el-button>
      </span>
    </el-dialog>

    <CoinChoiceDialog
      :visible.sync="showCoinChoiceDialog"
      :dialog-title="coinDialogTitle"
      title-icon="el-icon-video-camera-solid"
      :char-count="1"
      :coins-required="pendingCoinCost"
      :fee-summary-line="feeSummaryLine"
      :rule-text="coinDialogRuleText"
      :primary-button-text="coinDialogPrimaryText"
      char-label=""
      :choice-note="choiceNoteText"
      @use-coin="chooseUseCoin"
      @watch-video="chooseWatchVideo"
      @pay-direct="choosePayDirect"
    />

    <el-dialog center :before-close="function() {}" :show-close="false" :visible.sync="showPayDialog" width="680px" append-to-body>
      <pay :img="qrimg" :money="payMoney" :paytype="currentPayType"></pay>
      <span slot="footer">
        <el-button type="info" @click="paystatus" plain>已完成支付</el-button>
        <el-button type="info" @click="paystatus" plain>支付遇到问题</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { extractVideoUrlFromPastedText } from '../utils/extractVideoUrl'
import pay from './pay.vue'
import CoinChoiceDialog from './CoinChoiceDialog.vue'

export default {
  name: 'VideoDownloader',
  components: { pay, CoinChoiceDialog },
  data() {
    return {
      videoUrl: '',
      loading: false,
      result: {
        fileUrl: '',
        filename: '',
        title: '',
        panShareUrl: '',
        panSharePwd: '',
        panShareText: ''
      },
      showCoinChoiceDialog: false,
      showPayDialog: false,
      showLoginPrompt: false,
      pendingVideoUrl: '',
      pendingCoinCost: 5,
      qrimg: '',
      payMoney: 0.5,
      paymentOrderNo: '',
      isVideoDlPayment: false,
      currentPayType: 'wepay',
      videoDlChoiceNote: '说明：可使用金币抵扣下载、扫码看视频赚金币，或使用微信/支付宝按次付费。'
    }
  },
  computed: {
    coinChargeEnabled() {
      return this.$store.state.coinChargeEnabled !== false
    },
    pageIntroText() {
      return this.coinChargeEnabled
        ? '当前实测支持 B 站、小红书链接下载（仅限你有权下载的内容）。每次下载需先选择支付方式。'
        : '当前实测支持 B 站、小红书链接下载（仅限你有权下载的内容）。当前为限时免费模式，登录后可直接下载。'
    },
    infoAlertTitle() {
      return this.coinChargeEnabled
        ? '说明：后端依赖 yt-dlp。下载速度与服务器带宽、平台策略、视频码率等有关。抵扣金币后若下载失败会自动退回金币。'
        : '说明：后端依赖 yt-dlp。下载速度与服务器带宽、平台策略、视频码率等有关。当前为限时免费模式，登录后不扣金币。'
    },
    loginPromptText() {
      return this.coinChargeEnabled
        ? '视频下载需登录后选择支付方式（抵扣金币或扫码支付）。'
        : '视频下载需先登录。当前为限时免费模式，登录后可直接下载。'
    },
    feeSummaryLine() {
      if (!this.coinChargeEnabled) {
        return '当前为限时免费模式，登录后可直接下载，本次不扣金币。'
      }
      const n = typeof this.pendingCoinCost === 'number' ? this.pendingCoinCost : 5
      return `本次视频下载 1 次，需消耗 ${n} 金币（或等价扫码支付）。`
    },
    coinDialogTitle() {
      return this.coinChargeEnabled ? '选择支付方式' : '立即下载'
    },
    coinDialogRuleText() {
      return this.coinChargeEnabled
        ? '抵扣后若下载失败将自动退回金币；现金支付若下载失败请联系客服'
        : '当前为限时免费模式，本次不扣金币'
    },
    coinDialogPrimaryText() {
      return this.coinChargeEnabled ? '抵扣金币下载' : '立即免费下载'
    },
    choiceNoteText() {
      return this.coinChargeEnabled
        ? this.videoDlChoiceNote
        : '说明：当前为限时免费模式，登录后可直接下载，无需金币、扫码或额外支付。'
    },
    /** 与 Azure 语音合成右侧栏一致的加载文案风格 */
    panelLoadingHint() {
      if (!this.loading) return ''
      return '正在下载视频，请稍候…'
    }
  },
  methods: {
    authHeaders() {
      const token = this.$store.state.user && this.$store.state.user.token
      return token ? { Authorization: 'Bearer ' + token } : {}
    },
    refreshCoinsFromServer() {
      const user = this.$store.state.user
      const token = user && user.token
      if (!token) return
      this.$axios.get('/user/profile', { params: { token } }).then((res) => {
        if (res.data && res.data.coins != null) this.$store.commit('SetUserCoins', res.data.coins)
      }).catch(() => { /* ignore */ })
    },
    isCurrentlySupportedHost(inputUrl) {
      try {
        const host = (new URL(String(inputUrl || '').trim())).hostname.toLowerCase().replace(/^www\./, '')
        return (
          host === 'b23.tv'
          || host.endsWith('bilibili.com')
          || host.endsWith('xiaohongshu.com')
          || host === 'xhslink.com'
          || host.endsWith('xhslink.com')
          || host.endsWith('rednote.com')
        )
      } catch (e) {
        return false
      }
    },
    fullDownloadUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      let rel = String(url).trim()
      if (rel.startsWith('//')) rel = '/' + rel.replace(/^\/+/, '')
      if (!rel.startsWith('/')) rel = '/' + rel
      const base = String(this.$store.state.basicurl || '').replace(/\/+$/, '')
      return `${base}${rel}`
    },
    /**
     * 大视频用 axios+blob 会把整文件载入内存，易卡死或无反应；改为直链由浏览器流式下载（服务端已 Content-Disposition: attachment）。
     */
    saveBlobToFile() {
      const rel = this.result.fileUrl
      if (!rel) return
      const url = this.fullDownloadUrl(rel)
      if (!url) return
      try {
        const a = document.createElement('a')
        a.href = url
        a.target = '_blank'
        a.rel = 'noopener noreferrer'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        this.$message.success('已开始下载，请到浏览器下载列表查看进度（大文件请稍候）')
      } catch (e) {
        window.open(url, '_blank', 'noopener,noreferrer')
        this.$message.success('已打开下载链接，请在下载列表查看')
      }
    },
    saveToBaiduPan() {
      const filename = this.result.filename || ''
      if (!filename) {
        this.$message.warning('请先下载成功后再上传百度网盘')
        return
      }
      this.$axios.post('/cloud/baidu-pan/upload-share', {
        biz: 'video-downloader',
        filename
      }).then((res) => {
        const d = res.data || {}
        if (!d.success) {
          this.$message.warning(d.message || '上传百度网盘失败')
          return
        }
        const link = d.shareUrl || d.shareShortUrl || ''
        const shareText = (d.shareText && String(d.shareText).trim()) || ''
        this.result.panShareUrl = link
        this.result.panSharePwd = d.sharePwd || ''
        this.result.panShareText = shareText
        if (link) {
          const copyPayload = shareText || (d.sharePwd ? `${link} 提取码：${d.sharePwd}` : link)
          const alertBody = shareText
            ? `已生成分享口令（推荐整段复制）：\n\n${shareText}\n\n链接：${link}${d.sharePwd ? `\n提取码：${d.sharePwd}` : ''}`
            : `已生成百度网盘分享链接：\n${link}${d.sharePwd ? `\n提取码：${d.sharePwd}` : ''}`
          this.$alert(alertBody, '上传成功', {
            confirmButtonText: shareText ? '复制口令' : '复制链接',
            callback: () => {
              try {
                navigator.clipboard && navigator.clipboard.writeText(copyPayload)
              } catch (e) { /* ignore */ }
            }
          })
        } else {
          this.$message.warning('已上传到百度网盘，但未返回分享链接')
        }
      }).catch((err) => {
        const msg = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : '上传百度网盘失败，请稍后重试'
        this.$message.error(msg)
      })
    },
    goLogin() {
      this.showLoginPrompt = false
      this.$store.commit('SetShowLoginDialog', { show: true, tab: 'login' })
    },
    goRegister() {
      this.showLoginPrompt = false
      this.$store.commit('SetShowLoginDialog', { show: true, tab: 'register' })
    },
    startDownload() {
      const url = extractVideoUrlFromPastedText(this.videoUrl || '')
      if (!url) {
        this.$message.warning('请先粘贴视频链接或含链接的分享文案')
        return
      }
      if (!this.isCurrentlySupportedHost(url)) {
        this.$message.warning('当前仅支持 B 站、小红书链接；请更换后重试')
        return
      }
      if (!this.$store.state.user || !this.$store.state.user.token) {
        this.showLoginPrompt = true
        return
      }
      this.pendingVideoUrl = this.videoUrl
      if (!this.coinChargeEnabled) {
        this.pendingCoinCost = 0
        this.chooseUseCoin()
        return
      }
      this.$axios.get('/video-downloader/coin-cost').then((r) => {
        const c = r.data && r.data.coinCost
        this.pendingCoinCost = (typeof c === 'number' && c >= 0) ? c : 5
        if (this.pendingCoinCost === 0) {
          this.chooseUseCoin()
          return
        }
        this.showCoinChoiceDialog = true
      }).catch(() => {
        this.pendingCoinCost = 5
        this.showCoinChoiceDialog = true
      })
    },
    chooseUseCoin() {
      const token = this.$store.state.user && this.$store.state.user.token
      if (!token) {
        this.$message.warning('请先登录')
        return
      }
      this.showCoinChoiceDialog = false
      this.loading = true
      this.result = { fileUrl: '', filename: '', title: '', panShareUrl: '', panSharePwd: '', panShareText: '' }
      this.$axios.post('/video-downloader/reserve-coin', { url: this.pendingVideoUrl }, { headers: this.authHeaders() })
        .then((r) => {
          const d = r.data || {}
          if (!d.success || !d.entitlementId) {
            this.$message.warning(d.message || '扣减金币失败')
            return Promise.reject(new Error('__vdl_reserve_fail'))
          }
          if (d.coins != null) this.$store.commit('SetUserCoins', d.coins)
          return this.$axios.post('/video-downloader/download', {
            url: this.pendingVideoUrl,
            entitlementId: d.entitlementId
          }, { headers: this.authHeaders() })
        })
        .then((res) => {
          if (!res || !res.data) return
          const d = res.data
          if (!d.success || !d.fileUrl) {
            const hint = d.coinRefunded ? '（已退回金币）' : ''
            this.$message.warning((d.message || '下载失败') + hint)
            if (d.coins != null) this.$store.commit('SetUserCoins', d.coins)
            else this.refreshCoinsFromServer()
            return
          }
          this.result = {
            fileUrl: d.fileUrl,
            filename: d.filename || '',
            title: d.title || '',
            panShareUrl: '',
            panSharePwd: '',
            panShareText: ''
          }
          this.$message.success('下载完成，可点击「保存到本机」')
        })
        .catch((err) => {
          if (err && err.message === '__vdl_reserve_fail') return
          const data = err && err.response && err.response.data
          const msg = (data && data.message) || err.message || '下载失败，请稍后重试'
          if (data && data.coinRefunded) {
            this.$message.warning((data.message || '下载失败') + '（已退回金币）')
          } else {
            this.$message.error(msg)
          }
          if (data && data.coins != null) this.$store.commit('SetUserCoins', data.coins)
          else this.refreshCoinsFromServer()
        })
        .finally(() => {
          this.loading = false
        })
    },
    chooseWatchVideo() {
      this.showCoinChoiceDialog = false
      this.$store.commit('SetOpenEarnCoinDialog', true)
      this.$message.info('获取金币后可再次点击「开始下载」')
    },
    choosePayDirect(payType) {
      const token = this.$store.state.user && this.$store.state.user.token
      if (!token) {
        this.$message.warning('请先登录')
        return
      }
      this.showCoinChoiceDialog = false
      this.$axios.post('/video-downloader/prepare-pay', { url: this.pendingVideoUrl }, { headers: this.authHeaders() })
        .then((r) => {
          const d = r.data || {}
          if (!d.success || !d.vdlPayId) {
            this.$message.warning(d.message || '准备支付失败')
            return
          }
          const method = payType === 'wechat' ? 'wechat' : 'alipay'
          this.isVideoDlPayment = true
          this.currentPayType = payType === 'wechat' ? 'wepay' : 'alipay'
          const money = Number(d.money)
          return this.$axios.post('/qrpayment', {
            money,
            subject: '视频下载',
            payType: method,
            vdlPayId: d.vdlPayId
          }).then((qr) => {
            if (qr.data && (qr.data.code === 'WECHAT_NOT_AVAILABLE' || qr.data.code === 'WECHAT_ORDER_FAIL' || qr.data.code === 'WECHAT_ORDER_ERROR')) {
              this.$message.warning(qr.data.message || '支付暂不可用')
              this.isVideoDlPayment = false
              return
            }
            this.payMoney = money
            this.qrimg = qr.data.filename
            this.paymentOrderNo = qr.data.out_trade_no || qr.data.orderid
            this.showPayDialog = true
          })
        })
        .catch((err) => {
          this.isVideoDlPayment = false
          const data = err && err.response && err.response.data
          this.$message.warning((data && data.message) || err.message || '支付请求失败')
        })
    },
    paystatus() {
      this.showPayDialog = false
      if (!this.paymentOrderNo) return
      this.$axios.post('/queryOrder', { out_trade_no: this.paymentOrderNo }).then((res) => {
        if (res.data === '交易完成') {
          if (this.isVideoDlPayment) {
            this.loading = true
            this.result = { fileUrl: '', filename: '', title: '', panShareUrl: '', panSharePwd: '', panShareText: '' }
            this.$axios.post('/video-downloader/download', {
              url: this.pendingVideoUrl,
              payOrderId: this.paymentOrderNo
            }, { headers: this.authHeaders() })
              .then((r) => {
                const d = r.data || {}
                if (!d.success || !d.fileUrl) {
                  this.$message.warning((d.message || '下载失败') + '（订单已核销，如需退款请联系客服）')
                  return
                }
                this.result = {
                  fileUrl: d.fileUrl,
                  filename: d.filename || '',
                  title: d.title || '',
                  panShareUrl: '',
                  panSharePwd: '',
                  panShareText: ''
                }
                this.$message.success('下载完成，可点击「保存到本机」')
              })
              .catch((err) => {
                const data = err && err.response && err.response.data
                this.$message.error((data && data.message) || '下载失败')
              })
              .finally(() => {
                this.loading = false
                this.isVideoDlPayment = false
              })
            return
          }
          this.isVideoDlPayment = false
        } else {
          this.$message.warning(res.data || '未检测到支付完成')
        }
      }).catch(() => {
        this.$message.warning('查询支付状态失败')
      })
    }
  }
}
</script>

<style scoped>
.video-downloader-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 16px 20px 36px;
}
/* 与 TTS 页右侧参数区类似：有足够高度时加载遮罩覆盖主操作区，便于感知处理中 */
.video-downloader-panel {
  position: relative;
  min-height: 320px;
}
.login-prompt-text {
  margin: 0;
  line-height: 1.6;
  color: #606266;
}
.header h2 {
  margin: 0 0 8px;
  color: #303133;
}
.header h2 i {
  margin-right: 8px;
  color: #409eff;
}
.header p {
  margin: 0 0 14px;
  color: #606266;
}
.panel {
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}
.actions {
  display: flex;
  gap: 10px;
}
.info-alert {
  margin-top: 14px;
}
.support-tags {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.support-label {
  color: #606266;
  font-size: 13px;
}
.result {
  margin-top: 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
}
.result h3 {
  margin: 0 0 8px;
}
.result p {
  margin: 0 0 6px;
  color: #606266;
}
.save-btn {
  padding: 0;
  font-size: 14px;
}
.result-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.save-btn-pan {
  color: #e6a23c;
}
.pan-share-block {
  margin-top: 10px;
}
.pan-share-label {
  margin: 0 0 6px;
}
.pan-share-pre {
  margin: 0;
  padding: 10px 12px;
  background: #f5f7fa;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: #303133;
  border: 1px solid #ebeef5;
}
.pan-share-line {
  margin-top: 8px;
  word-break: break-all;
}
@media (max-width: 768px) {
  .video-downloader-page {
    padding: 12px 12px 24px;
  }
  .actions {
    flex-direction: column;
  }
  .video-downloader-panel {
    min-height: 280px;
  }
}
</style>

<style lang="less">
/* 与 Azuretts 右侧 .right 暗黑背景一致，加载层下的卡片不刺眼 */
.dark-mode .video-downloader-page .panel.video-downloader-panel {
  background: var(--bg-card);
}
</style>
